// @flow
import React, { PureComponent } from 'react';
import type { Node } from 'react';

// Styles
import styles from 'components/Specifications.scss';
import { connect } from 'react-redux';
import { toCapitalize } from 'Common/string';
import PropTypes from 'prop-types';
import { valueOrNA } from 'Common/valueOrNA';
import slugify from 'slugify';

type DataImplementation = {
  title: string,
  data: { label: string, value: any }[]
};

/**
 * <Specifications /> component.
 */
class Specifications extends PureComponent<*, *> {
  /**
   * Implements propTypes().
   *
   * @return {Object} - propType object.
   */
  static propTypes = {
    data: PropTypes.shape({
      year: PropTypes.number,
      mileage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      fuelType: PropTypes.string,
      transmission: PropTypes.string,
      seats: PropTypes.string,
      engineSize: PropTypes.string
    }),
    specificationsName: PropTypes.string,
    item: PropTypes.object
  };

  /**
   * MapStateToProps implementation.
   * @return {object} new state object
   * @param {object} state object
   */
  static mapStateToProps(state: Object) {
    return {
      item: state.listing
    };
  }

  /**
   * getData implementation.
   * @return {object} data
   * @param {string} specificationsName
   */
  getData: (
    specificationsName: string
  ) => DataImplementation | void = specificationsName => {
    const { item } = this.props;
    switch (specificationsName) {
      case 'runningCosts':
        return {
          title: 'Running costs',
          data: [
            {
              label: 'Average mpg',
              value: `${valueOrNA(item.technical, 'ec_combined_mpg')} mpg`
            },
            {
              label: 'CO2 emissiones',
              value: `${valueOrNA(item.technical, 'co2_gkm')} g/km`
            },
            {
              label: 'Annual Tax',
              value:
                item.derivative && item.derivative.tax
                  ? `£${valueOrNA(
                      item.derivative.tax,
                      '12month'
                    ).toLocaleString('en')}`
                  : 'N/A'
            },
            {
              label: 'Insurance group',
              value: valueOrNA(item.technical, 'insuranceGroup')
            }
          ]
        };

      case 'performance':
        return {
          title: 'Performance',
          data: [
            {
              label: 'Engine power',
              value: `${valueOrNA(item.technical, 'engine_power_bhp')} bhp`
            },
            {
              label: 'Engine size',
              value: `${valueOrNA(item.technical, 'cc')} cc`
            },
            {
              label: 'Acceleration 0-62',
              value: `${valueOrNA(item.technical, '0_to_62_mph_secs')} secs`
            },
            {
              label: 'Top speed',
              value: `${valueOrNA(item.technical, 'top_speed')} mph`
            }
          ]
        };
      case 'exterior':
        return {
          title: 'Exterior',
          data: [
            {
              label: 'Body style',
              value: toCapitalize(valueOrNA(item.derivative, 'bodyStyle'))
            },
            {
              label: 'Colour',
              value: toCapitalize(valueOrNA(item, 'colour'))
            },
            {
              label: 'Doors',
              value: toCapitalize(valueOrNA(item.derivative, 'doors'))
            }
          ]
        };
      case 'interior':
        return {
          title: 'Interior',
          data: [
            {
              label: 'Luggage capacity seats down',
              value: `${valueOrNA(
                item.technical,
                'luggage_capacity_seats_down'
              )} ltrs`
            },
            {
              label: 'Luggage capacity seats up',
              value: `${valueOrNA(
                item.technical,
                'luggage_capacity_seats_up'
              ).toLocaleString('en')} ltrs`
            },
            {
              label: 'Seats',
              value: valueOrNA(item.technical, 'no_of_seats')
            }
          ]
        };
      case 'fuel':
        return {
          title: 'Fuel',
          data: [
            {
              label: 'Fuel type',
              value: toCapitalize(valueOrNA(item.derivative, 'fuelType'))
            },
            {
              label: 'Fuel delivery',
              value: toCapitalize(valueOrNA(item.derivative, 'fuelDelivery'))
            },
            {
              label: 'Fuel tank capacity',
              value: `${valueOrNA(
                item.technical,
                'fuel_tank_capacity_litres'
              )} ltrs`
            }
          ]
        };
      case 'transmission':
        return {
          title: 'Transmission',
          data: [
            {
              label: 'Drive train',
              value:
                valueOrNA(item.derivative, 'driveTrain') !== 'N/A'
                  ? toCapitalize(
                      valueOrNA(item.derivative, 'driveTrain').toLowerCase()
                    )
                  : 'N/A'
            },
            {
              label: 'Gears',
              value:
                valueOrNA(item.technical, 'gears') !== 'N/A'
                  ? toCapitalize(valueOrNA(item.technical.gears).toLowerCase())
                  : 'N/A'
            },
            {
              label: 'Type',
              value:
                valueOrNA(item.technical, 'transmission') !== 'N/A'
                  ? toCapitalize(
                      valueOrNA(item.technical.transmission).toLowerCase()
                    )
                  : 'N/A'
            }
          ]
        };
    }
  };

  /**
   * Renders <Specifications /> component.
   *
   * @return {JSX} - renders Specifications.
   */
  render(): Node {
    const { classSpecifications } = styles;
    if (!this.props.item) {
      return null;
    }
    // $FlowFixMe these can be undefined
    const { title, data } = this.getData(this.props.specificationsName);
    return (
      <div
        className={classSpecifications}
        name={
          this.props.specificationsName
            ? slugify(this.props.specificationsName)
            : null
        }
      >
        <h4>{title}</h4>
        <ul>
          {data.map((item, i) => (
            <li key={i}>
              <div>{item.label}</div>
              <div> {item.value}</div>
            </li>
          ))}
        </ul>
      </div>
    );
  }
}

// $FlowFixMe
export default connect(Specifications.mapStateToProps, null)(Specifications);



// WEBPACK FOOTER //
// ./src/Detail/Specifications/index.js